/* smart-answer-card-body-complete.tsx generated by @compiled/babel-plugin v0.38.1 */
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./smart-answer-card-body-complete.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { getFrontendExperiment } from '@atlassian/search-experiment';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartAnswerFooterSection } from '../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import { getSmartLinksAnalyticsContextData, onAIAnswerResultCompleteShown, onAIAnswerSourceClick, onAIAnswerTabClick, SmartLinksAnalyticsContextLocation } from '../../ai-answer-dialog/analytics';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../common/ui/ai-sources';
import { getSAINLayerFrontendMutiArmExperiment } from '../../common/utils/get-sain-layer-frontend-experiment';
import { AIAnswerTab, aiAnswerTabsOrder, QueryIntent } from '../../constants';
import { isHello } from '../../utils';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { AITabs } from '../common/ui/ai-tabs';
import { AITabsHeader, AITabsHeaderWithSourcesStack } from '../common/ui/ai-tabs-header';
import { AITabsPanel } from '../common/ui/ai-tabs-panel';
import { AITabsSources } from '../common/ui/ai-tabs-sources';
import { ReactRenderer } from '../common/ui/react-renderer';
import { ReadMoreButton } from '../common/ui/read-more-button';
import { messages } from './messages';
import { SmartAnswerCardBodyTabAIAnswer } from './smart-answer-card-body-tab-ai-answer';
var styles = {
  fullHeight: "_4t3i1osq",
  sectionContainer: "_n3tdutpp _19bvpxbi _u5f3pxbi _ca0q1b66",
  explicitQuestion: "_1tke1edt"
};
export function SmartAnswerCardBodyComplete(_ref) {
  var collapsed = _ref.collapsed,
    onExpandCollapseButtonClick = _ref.onExpandCollapseButtonClick,
    queryIntent = _ref.queryIntent;
  var _useState = useState(AIAnswerTab.AI_ANSWER),
    _useState2 = _slicedToArray(_useState, 2),
    selectedTab = _useState2[0],
    setSelectedTab = _useState2[1];
  var _useAIAnswerContext = useAIAnswerContext(),
    callbacks = _useAIAnswerContext.callbacks,
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var answerString = commonAttributes.answerString,
    cloudId = commonAttributes.cloudId,
    sources = commonAttributes.sources,
    errorStateSuggestedQueries = commonAttributes.errorStateSuggestedQueries,
    searchSessionId = commonAttributes.searchSessionId;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var onHello = isHello(cloudId);
  var renderedContentRef = useRef(null);
  var contentContainerRef = useRef(null);
  var completeAnswerAnalyticsEventFiredRef = useRef(false);

  // Detect if content is truncated when collapsed
  var isContentTruncated = useContentTruncationDetector(renderedContentRef, contentContainerRef, collapsed);
  useEffect(function () {
    var _contentContainerRef$, _contentContainerRef$2;
    // Only fire once and ensure DOM element is ready with meaningful content
    if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
      return;
    }
    var answerHeight = renderedContentRef.current.getBoundingClientRect().height;
    var containerHeight = (_contentContainerRef$ = (_contentContainerRef$2 = contentContainerRef.current) === null || _contentContainerRef$2 === void 0 ? void 0 : _contentContainerRef$2.getBoundingClientRect().height) !== null && _contentContainerRef$ !== void 0 ? _contentContainerRef$ : 0;
    var whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

    // Only fire if we have meaningful height (content is rendered)
    if (answerHeight > 0 && containerHeight > 0) {
      fireAnalyticsEvent(onAIAnswerResultCompleteShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        answerHeight: answerHeight,
        containerHeight: containerHeight,
        whitespaceHeight: whitespaceHeight,
        queryIntent: queryIntent
      })));
      completeAnswerAnalyticsEventFiredRef.current = true;
    }
  }, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);
  var _getFrontendExperimen = getFrontendExperiment({
      abTestId: 'kd_sain_show_no_answer_suggestions_exp'
    }),
    serveErrorStateSuggestedQueriesExp = _getFrontendExperimen.serveExperiment;
  var _getSAINLayerFrontend = getSAINLayerFrontendMutiArmExperiment({
      abTestId: 'kd_sain_sources_redesign_pt_2'
    }),
    cohortSourcesRedesignExperimentPt2 = _getSAINLayerFrontend.cohort;
  var showReadMoreButton = selectedTab === AIAnswerTab.AI_ANSWER && collapsed && isContentTruncated;
  useEffect(function () {
    fireSAINUsageEvent({
      commonAttributes: commonAttributes
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var handleTabChange = useCallback(function (selectedTabIndex) {
    var selectedTab = aiAnswerTabsOrder[selectedTabIndex];
    setSelectedTab(selectedTab);
    fireAnalyticsEvent(onAIAnswerTabClick(_objectSpread(_objectSpread({}, commonAttributes), {}, {
      selectedTab: selectedTab
    })));
  }, [setSelectedTab, fireAnalyticsEvent, commonAttributes]);
  var sourceClickCallback = useCallback(function (index, source, hostname) {
    var _callbacks$onAIAnswer;
    fireAnalyticsEvent(onAIAnswerSourceClick(_objectSpread({
      sourceIndex: index,
      hostname: hostname,
      sourceProduct: source.product || ''
    }, commonAttributes)));
    fireSAINUsageEvent({
      metCriteria: SainAction.SOURCE_CLICKED,
      commonAttributes: commonAttributes,
      sourceProduct: source.product || ''
    });
    callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerSourceClick) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
  }, [fireAnalyticsEvent, commonAttributes, callbacks, fireSAINUsageEvent]);
  if (onHello || cohortSourcesRedesignExperimentPt2 === 'sources_under_answer') {
    return /*#__PURE__*/React.createElement(Stack, {
      alignBlock: "stretch",
      grow: "fill",
      spread: "space-between",
      xcss: cx(styles.fullHeight, queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion)
    }, /*#__PURE__*/React.createElement(Stack, {
      alignBlock: "stretch",
      grow: "fill",
      spread: "space-between",
      xcss: styles.fullHeight
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AICollapse, {
      title: /*#__PURE__*/React.createElement(Heading, {
        as: "h2",
        size: "xxsmall"
      }, /*#__PURE__*/React.createElement(Text, {
        color: "color.text.subtlest",
        size: "small",
        weight: "bold"
      }, /*#__PURE__*/React.createElement(FormattedMessage, messages.smart_answer_card_title))),
      isCollapsed: collapsed,
      onExpandCollapse: onExpandCollapseButtonClick,
      ref: contentContainerRef,
      showReadMoreButton: showReadMoreButton,
      queryIntent: queryIntent
    }, /*#__PURE__*/React.createElement(AnalyticsContext, {
      data: getSmartLinksAnalyticsContextData({
        location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
        commonAttributes: commonAttributes
      })
    }, /*#__PURE__*/React.createElement(SmartCardProvider, null, /*#__PURE__*/React.createElement(Box, {
      id: "ai-answer-result-section",
      ref: renderedContentRef
    }, /*#__PURE__*/React.createElement(ReactRenderer, {
      markdown: answerString
    }))))), showReadMoreButton && /*#__PURE__*/React.createElement(ReadMoreButton, {
      onClick: onExpandCollapseButtonClick
    })), !collapsed && sources.length > 0 ? /*#__PURE__*/React.createElement(Box, {
      xcss: styles.sectionContainer
    }, /*#__PURE__*/React.createElement(AISourcesCarousel, {
      sources: sources,
      searchSessionId: searchSessionId,
      onClickHandler: sourceClickCallback
    })) : null, /*#__PURE__*/React.createElement(Box, {
      xcss: styles.sectionContainer
    }, /*#__PURE__*/React.createElement(AIDisclaimerAndFeedback, {
      showFeedbackButtons: true,
      showCopyButton: true,
      renderedContentRef: renderedContentRef
    }))), !collapsed || errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp ? /*#__PURE__*/React.createElement(SmartAnswerFooterSection, {
      showOpenChat: true
    }) : null);
  }
  return /*#__PURE__*/React.createElement(Stack, {
    alignBlock: "stretch",
    grow: "fill",
    spread: "space-between",
    xcss: cx(styles.fullHeight, queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion)
  }, /*#__PURE__*/React.createElement(Stack, {
    alignBlock: "stretch",
    grow: "fill",
    spread: "space-between",
    xcss: styles.fullHeight
  }, /*#__PURE__*/React.createElement(AITabs, {
    onChange: handleTabChange,
    selectedTabIndex: aiAnswerTabsOrder.indexOf(selectedTab)
  }, cohortSourcesRedesignExperimentPt2 === 'sources_tab_redesign' ? /*#__PURE__*/React.createElement(AITabsHeaderWithSourcesStack, {
    collapsed: collapsed,
    onExpandCollapseButtonClick: onExpandCollapseButtonClick,
    sources: sources
  }) : /*#__PURE__*/React.createElement(AITabsHeader, {
    collapsed: collapsed,
    onExpandCollapseButtonClick: onExpandCollapseButtonClick,
    showSourcesTab: sources.length > 0
  }), /*#__PURE__*/React.createElement(AITabsPanel, {
    collapsed: collapsed,
    ref: contentContainerRef,
    showReadMoreButton: showReadMoreButton,
    queryIntent: queryIntent
  }, /*#__PURE__*/React.createElement(SmartAnswerCardBodyTabAIAnswer, {
    ref: renderedContentRef
  })), sources.length > 0 ? /*#__PURE__*/React.createElement(AITabsPanel, {
    collapsed: collapsed,
    queryIntent: queryIntent,
    showReadMoreButton: showReadMoreButton,
    ref: contentContainerRef
  }, /*#__PURE__*/React.createElement(AITabsSources, null)) : null), showReadMoreButton && /*#__PURE__*/React.createElement(ReadMoreButton, {
    onClick: onExpandCollapseButtonClick
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: styles.sectionContainer
  }, /*#__PURE__*/React.createElement(AIDisclaimerAndFeedback, {
    showFeedbackButtons: true,
    showCopyButton: true,
    renderedContentRef: renderedContentRef
  }))), (!collapsed || errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp) && selectedTab === AIAnswerTab.AI_ANSWER ? /*#__PURE__*/React.createElement(SmartAnswerFooterSection, {
    showOpenChat: true
  }) : null);
}